import { mergeCanAccess } from './companyFeatures.js';

export const CONFIGURABLE_ROLES = ['Accountant', 'Agent', 'Hub Admin'];

/**
 * True = this role already has that sidebar item.
 * Dashboard stays on for every role and is not listed here.
 */
export const ROLE_MODULE_DEFAULTS = {
  Accountant: {
    shipments: true,
    dispatch: true,
    crossing: true,
    update_status: true,
    track_shipment: true,
    settlements: true,
    cashbook: true,
    expenses: true,
    mis_reports: true,
    analytical_reports: true,
    attendance: true,
    users: false,
    loans: false,
    commission: false,
    on_contract: false,
    on_contract_truck: false,
    location_hub: false,
    my_partnerships: false,
    external_ml: false,
    agent_network: false,
  },
  Agent: {
    shipments: true,
    dispatch: true,
    crossing: true,
    update_status: true,
    track_shipment: true,
    settlements: true,
    cashbook: true,
    users: true,
    expenses: true,
    mis_reports: true,
    analytical_reports: true,
    attendance: true,
    my_partnerships: true,
    loans: false,
    commission: false,
    on_contract: false,
    on_contract_truck: false,
    location_hub: false,
    external_ml: false,
    agent_network: false,
  },
  'Hub Admin': {
    shipments: true,
    dispatch: true,
    crossing: true,
    update_status: true,
    track_shipment: true,
    cashbook: true,
    location_hub: true,
    users: true,
    attendance: true,
    expenses: true,
    mis_reports: true,
    analytical_reports: true,
    external_ml: true,
    settlements: false,
    loans: false,
    commission: false,
    on_contract: false,
    on_contract_truck: false,
    my_partnerships: false,
    agent_network: false,
  },
};

export const ROLE_MODULE_LABELS = {
  shipments: 'Shipments',
  dispatch: 'Dispatch',
  crossing: 'Crossing',
  update_status: 'Update Status',
  track_shipment: 'Track Shipment',
  settlements: 'Settlements',
  users: 'User Management',
  expenses: 'Expense Management',
  mis_reports: 'MIS Reports',
  analytical_reports: 'Analytical Reports',
  attendance: 'Attendance',
  cashbook: 'Cashbook',
  loans: 'Loans',
  commission: 'Shipment Commission',
  on_contract: 'On Contract',
  on_contract_truck: 'Contract Management',
  location_hub: 'Location Hub & Vehicle',
  my_partnerships: 'My Partnerships',
  external_ml: 'External ML Sheet',
  agent_network: 'Agent Network',
};

export const ROLE_MODULE_ORDER = [
  'shipments',
  'dispatch',
  'crossing',
  'update_status',
  'track_shipment',
  'settlements',
  'users',
  'expenses',
  'loans',
  'cashbook',
  'attendance',
  'mis_reports',
  'analytical_reports',
  'location_hub',
  'commission',
  'on_contract',
  'on_contract_truck',
  'external_ml',
  'agent_network',
  'my_partnerships',
];

/** Direct URLs that belong to a sidebar item. Longest prefix wins. */
export const MODULE_ROUTE_PREFIXES = [
  ['/external-commission-dashboard', 'commission'],
  ['/external-commission-breakdown', 'commission'],
  ['/agent-commission-breakdown', 'commission'],
  ['/ml-commission-payments', 'commission'],
  ['/external-commissions', 'commission'],
  ['/commission-analytics', 'commission'],
  ['/company-settlement-approvals', 'settlements'],
  ['/company-settlements', 'settlements'],
  ['/record-agent-settlement', 'settlements'],
  ['/ogcl-transfer-requests', 'dispatch'],
  ['/missing-shipments', 'shipments'],
  ['/shipment-crossing', 'crossing'],
  ['/daily-earnings', 'shipments'],
  ['/shipment-clients', 'shipments'],
  ['/user/track-shipment', 'track_shipment'],
  ['/vehicle-contracts', 'on_contract_truck'],
  ['/contract-parties', 'on_contract_truck'],
  ['/my-partnerships', 'my_partnerships'],
  ['/vehicle-owners', 'location_hub'],
  ['/location-hubs', 'location_hub'],
  ['/partner-agents', 'agent_network'],
  ['/loans-received', 'loans'],
  ['/update-status', 'update_status'],
  ['/external-ml', 'external_ml'],
  ['/loans-given', 'loans'],
  ['/on-contracts', 'on_contract'],
  ['/ml-sheets', 'dispatch'],
  ['/attendance', 'attendance'],
  ['/analytics', 'analytical_reports'],
  ['/shipments', 'shipments'],
  ['/expenses', 'expenses'],
  ['/salaries', 'attendance'],
  ['/cashbook', 'cashbook'],
  ['/payments', 'shipments'],
  ['/vehicles', 'location_hub'],
  ['/reports', 'mis_reports'],
  ['/returns', 'shipments'],
  ['/routes', 'location_hub'],
  ['/users', 'users'],
];

const SIDEBAR_ID_MODULE = {
  shipments: 'shipments',
  masterLoadingSheet: 'dispatch',
  masterLoadingSheetExternal: 'dispatch',
  shipmentCrossing: 'crossing',
  updateStatus: 'update_status',
  trackShipment: 'track_shipment',
  cashbook: 'cashbook',
  externalMLSheet: 'external_ml',
  settlements: 'settlements',
  locationHubVehicle: 'location_hub',
  shipmentCommission: 'commission',
  userManagement: 'users',
  hrWorkforce: 'attendance',
  expenseManagement: 'expenses',
  misReports: 'mis_reports',
  analyticalReports: 'analytical_reports',
  onContract: 'on_contract',
  contractManagement: 'on_contract_truck',
  agentNetwork: 'agent_network',
  myPartnerships: 'my_partnerships',
};

function companyOffersModule(role, moduleKey, features, options) {
  const shipment = Boolean(features.shipment);
  const expenses = Boolean(features.expenses);
  const attendance = Boolean(features.attendance) && options.networkMode !== 'EXTERNAL';
  const crossingOn = options.crossingEnabled !== false && options.networkMode !== 'EXTERNAL';

  switch (moduleKey) {
    case 'shipments':
    case 'dispatch':
    case 'update_status':
    case 'track_shipment':
    case 'settlements':
      return shipment;
    case 'crossing':
      return shipment && crossingOn;
    case 'users':
      if (role === 'Hub Admin') return true;
      return shipment || attendance;
    case 'expenses':
      return expenses;
    case 'attendance':
      return attendance;
    case 'cashbook':
      return Boolean(features.cashbook);
    case 'loans':
      return Boolean(features.loans);
    case 'commission':
      return Boolean(features.commission || features.external_commission);
    case 'analytical_reports':
      return features.analytical_reports !== false;
    case 'mis_reports':
      return true;
    case 'on_contract':
      return Boolean(features.on_contract);
    case 'on_contract_truck':
      return Boolean(features.on_contract_truck);
    case 'my_partnerships':
      return Boolean(features.my_partnerships);
    case 'location_hub':
      return shipment;
    case 'external_ml':
      return Boolean(features.external_ml);
    case 'agent_network':
      return Boolean(features.agent_network) && options.networkMode === 'EXTERNAL';
    default:
      return false;
  }
}

/** Checkboxes for Company Settings: current sidebar items, already selected. */
export function mergeRoleModuleAccess(companyFeatures, stored, options = {}) {
  const features = mergeCanAccess(companyFeatures);
  const saved = stored && typeof stored === 'object' ? stored : {};
  const next = {};
  CONFIGURABLE_ROLES.forEach((role) => {
    const roleSaved = saved[role] && typeof saved[role] === 'object' ? saved[role] : {};
    next[role] = {};
    ROLE_MODULE_ORDER.forEach((moduleKey) => {
      if (ROLE_MODULE_DEFAULTS[role][moduleKey] !== true) return;
      if (!companyOffersModule(role, moduleKey, features, options)) return;
      next[role][moduleKey] = roleSaved[moduleKey] === false ? false : true;
    });
  });
  return next;
}

export function isRoleModuleEnabled(user, moduleKey) {
  if (!user) return false;
  const role = user.role;
  if (role === 'System Admin' || role === 'Admin') return true;
  const defaults = ROLE_MODULE_DEFAULTS[role];
  if (!defaults) return true;
  if (defaults[moduleKey] !== true) return false;
  const roleMap = user.role_module_access?.[role];
  if (roleMap && roleMap[moduleKey] === false) return false;
  return true;
}

export function moduleForPath(pathname) {
  const path = pathname || '';
  const match = MODULE_ROUTE_PREFIXES.find(([prefix]) => (
    path === prefix || path.startsWith(`${prefix}/`)
  ));
  return match ? match[1] : null;
}

export function sidebarItemModule(itemId) {
  return SIDEBAR_ID_MODULE[itemId] || null;
}
